import React, { useCallback, useEffect, useRef, useState } from 'react';
import './product-editor-modal.css';
import AdminToggle from './AdminToggle';
import ReturnAllowanceSelect from './ReturnAllowanceSelect';
import { fileToCatalogImageDataUrl } from './imageHelpers';
import { computeSalePriceForSave, productSalePrice } from '../productPricing';
import {
  defaultProduct,
  slugInList,
  normalizePriceTyping,
  parsePriceText,
  generateMeaningfulProductId,
} from './adminUtils';
import { hrefForSameSiteAsset, pathLooksLikeVideo } from '../productMediaUtils';
import { normalizeProductGiftTags } from '../productGiftTags';

function sortedTaxa(list) {
  return [...(list || [])].sort((a, b) => a.sort_order - b.sort_order || a.slug.localeCompare(b.slug));
}

/** Lines for "more photos" field: gallery paths excluding primary `image` when it matches first slot. */
function galleryExtrasForForm(p) {
  const main = String(p?.image || '').trim();
  const arr = Array.isArray(p?.images) ? p.images.map((x) => String(x || '').trim()).filter(Boolean) : [];
  if (arr.length === 0) return [];
  if (main && arr[0] === main) return arr.slice(1);
  if (!main) return arr;
  return arr.filter((u) => u !== main);
}

function parseMultiline(raw) {
  return String(raw || '')
    .split(/[\r\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function editorSnapshot(form, priceDraft, stockUnlimited, stockQtyDraft, saleOn, salePriceDraft, salePercentDraft) {
  return JSON.stringify({
    name: String(form.name || '').trim(),
    id: String(form.id || '').trim(),
    type: String(form.type || '').trim(),
    subcategory: String(form.subcategory || '').trim(),
    description: String(form.description || '').trim(),
    image: String(form.image || '').trim(),
    imagesRaw: String(form.imagesRaw || '').trim(),
    video: String(form.video || '').trim(),
    videosRaw: String(form.videosRaw || '').trim(),
    priceDraft: String(priceDraft || '').trim(),
    saleOn: !!saleOn,
    salePriceDraft: String(salePriceDraft || '').trim(),
    salePercentDraft: String(salePercentDraft || '').trim(),
    noSound: form.noSound !== false,
    is_visible: form.is_visible !== false,
    stockUnlimited: !!stockUnlimited,
    stockQtyDraft: String(stockQtyDraft || '').trim(),
    char_personalization: !!form.char_personalization,
    char_personalization_letters: String(form.char_personalization_letters || '').trim(),
    gift_tags: normalizeProductGiftTags(form.gift_tags).join(','),
    return_allowance: String(form.return_allowance || 'inherit'),
  });
}

function editorListThumbSrc(path) {
  const s = String(path || '').trim();
  if (!s || s.startsWith('data:') || s.length > 800 || pathLooksLikeVideo(s)) return '';
  return hrefForSameSiteAsset(s);
}

export default function ProductEditorModal({
  show,
  mode,
  categoryMeta,
  initialProduct,
  existingProductIds = [],
  canUploadToServer,
  uploadServerImage,
  uploadServerVideo,
  onClose,
  onSave,
  setStatus,
}) {
  const [form, setForm] = useState(() => ({ ...defaultProduct }));
  /** Text field for price so decimals and typing behave like a normal amount box (not spinner-only number input). */
  const [priceDraft, setPriceDraft] = useState('0');
  const [saleOn, setSaleOn] = useState(false);
  const [salePriceDraft, setSalePriceDraft] = useState('');
  const [salePercentDraft, setSalePercentDraft] = useState('');
  const [stockUnlimited, setStockUnlimited] = useState(true);
  const [stockQtyDraft, setStockQtyDraft] = useState('10');
  /** In-modal feedback so errors are readable on top of the dimmed page (global status sits behind the overlay). */
  const [modalBanner, setModalBanner] = useState(null);
  const [mediaKind, setMediaKind] = useState('photo');
  const [mediaSlot, setMediaSlot] = useState('main');
  const [extraPhotoUrlDraft, setExtraPhotoUrlDraft] = useState('');
  const [extraVideoUrlDraft, setExtraVideoUrlDraft] = useState('');
  const subs = sortedTaxa(categoryMeta?.subcategories);
  const productTypes = sortedTaxa(categoryMeta?.types);
  const baselineSnapshotRef = useRef('');
  const [autoProductId, setAutoProductId] = useState('');
  /** Select value for subcategory (list slug, __custom__, or empty) — separate from typed custom slug in form.subcategory */
  const [subPick, setSubPick] = useState('');

  /** Upload / embed feedback: show only in this modal (not catalog Status — that sits under the dimmed overlay). */
  const setModalOnly = (variant, text) => {
    setModalBanner({ variant, text });
  };

  /** Validation etc.: also update catalog page Status. */
  const setModalAndCatalogStatus = (variant, text) => {
    setModalBanner({ variant, text });
    setStatus(text);
  };

  useEffect(() => {
    if (show) {
      setModalBanner(null);
      setMediaKind('photo');
      setMediaSlot('main');
      setExtraPhotoUrlDraft('');
      setExtraVideoUrlDraft('');
    }
  }, [show]);

  useEffect(() => {
    if (!show || !categoryMeta) return;
    const p = { ...defaultProduct, ...initialProduct };
    const extraVid = Array.isArray(p.videos) ? p.videos.map((x) => String(x || '').trim()).filter(Boolean) : [];
    const { videos: _drop, images: _dropImg, ...base } = p;
    const subsLocal = sortedTaxa(categoryMeta.subcategories);
    let initialSub = String(p.subcategory || '').trim();
    if (mode === 'add' && !initialSub) {
      initialSub = subsLocal.length ? String(subsLocal[0].slug || '') : 'all';
    }
    const pr = Number(p.price);
    const priceStr = Number.isFinite(pr) && pr >= 0 ? String(parseFloat(pr.toFixed(2))) : '0';
    setPriceDraft(priceStr);
    const sale = productSalePrice({ price: pr, sale_price: p.sale_price });
    setSaleOn(sale !== null);
    setSalePriceDraft(sale !== null ? String(sale) : '');
    setSalePercentDraft('');
    const sq = p.stock_qty;
    setStockUnlimited(sq === null || sq === undefined || sq === '');
    setStockQtyDraft(sq === null || sq === undefined || sq === '' ? '10' : String(Math.max(0, parseInt(sq, 10) || 0)));

    const stockIsUnlimited = sq === null || sq === undefined || sq === '';
    const stockQtyStr = stockIsUnlimited ? '10' : String(Math.max(0, parseInt(sq, 10) || 0));
    const nextForm = {
      ...base,
      video: String(p.video || ''),
      videosRaw: extraVid.join('\n'),
      imagesRaw: galleryExtrasForForm(p).join('\n'),
      noSound: p.noSound !== false,
      subcategory: initialSub,
      is_visible: p.is_visible !== false,
      char_personalization: Boolean(p.char_personalization),
      char_personalization_letters: String(p.char_personalization_letters || '').trim(),
      gift_tags: normalizeProductGiftTags(p.gift_tags),
      return_allowance: p.return_allowance || 'inherit',
    };
    let nextSubPick = '';
    if (subsLocal.length === 0) {
      nextSubPick = initialSub || 'all';
    } else if (!initialSub) {
      nextSubPick = '';
    } else if (slugInList(subsLocal, initialSub)) {
      nextSubPick = initialSub;
    } else {
      nextSubPick = '__custom__';
    }
    setSubPick(nextSubPick);
    setForm(nextForm);
    baselineSnapshotRef.current = editorSnapshot(
      nextForm,
      priceStr,
      stockIsUnlimited,
      stockQtyStr,
      sale !== null,
      sale !== null ? String(sale) : '',
      ''
    );
  }, [show, initialProduct, mode, categoryMeta]);

  useEffect(() => {
    if (!show || mode !== 'add' || !categoryMeta) {
      setAutoProductId('');
      return;
    }
    let sub = String(form.subcategory || '').trim();
    if (!sub) sub = subs.length ? '' : 'all';
    const id = generateMeaningfulProductId(categoryMeta.slug, sub, existingProductIds);
    setAutoProductId(id);
  }, [show, mode, categoryMeta, form.subcategory, subs.length, existingProductIds]);

  const formIsDirty =
    show &&
    baselineSnapshotRef.current !== '' &&
    editorSnapshot(form, priceDraft, stockUnlimited, stockQtyDraft, saleOn, salePriceDraft, salePercentDraft) !==
    baselineSnapshotRef.current;

  const requestClose = useCallback(() => {
    if (formIsDirty && !window.confirm('Discard unsaved product changes?')) {
      return;
    }
    onClose();
  }, [formIsDirty, onClose]);

  useEffect(() => {
    if (!show) return;
    const onKey = (e) => {
      if (e.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [show, requestClose]);

  const fileInputDisabled =
    mediaKind === 'video'
      ? !canUploadToServer || !uploadServerVideo
      : mediaSlot === 'extra'
        ? !canUploadToServer || !uploadServerImage
        : false;

  if (!show) return null;

  if (!categoryMeta) {
    return (
      <div
        className="modal fade show d-block position-fixed top-0 start-0 w-100 h-100"
        style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1100 }}
        role="presentation"
      >
        <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title mb-0">Cannot open product editor</h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
            </div>
            <div className="modal-body">
              <p className="mb-0">
                That category is missing from the catalog. Pick another category on the Products tab, or refresh the
                page.
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleEmbed = async (file) => {
    if (!file) return;
    setModalOnly('info', 'Adding photo…');
    try {
      const dataUrl = await fileToCatalogImageDataUrl(file);
      setForm((f) => ({ ...f, image: dataUrl }));
      setModalOnly('success', 'Photo embedded in catalog.');
    } catch (e) {
      setModalOnly('danger', e.message || 'Could not add photo.');
    }
  };

  const handleServerUpload = async (file) => {
    if (!file || !uploadServerImage) return;
    if (!canUploadToServer) {
      setModalOnly('warning', 'Sign in under Admin sign-in to enable server upload.');
      return;
    }
    setModalOnly('info', 'Uploading…');
    try {
      const path = await uploadServerImage(file);
      setForm((f) => ({ ...f, image: path }));
      setModalOnly('success', 'Image saved on server.');
    } catch (e) {
      setModalOnly('danger', e.message || 'Upload failed.');
    }
  };

  const handleServerImageAppend = async (file) => {
    if (!file || !uploadServerImage) return;
    if (!canUploadToServer) {
      setModalOnly('warning', 'Sign in under Admin sign-in to enable server upload.');
      return;
    }
    setModalOnly('info', 'Uploading photo…');
    try {
      const path = await uploadServerImage(file);
      setForm((f) => {
        const lines = String(f.imagesRaw || '')
          .split(/[\r\n,]+/)
          .map((s) => s.trim())
          .filter(Boolean);
        if (!lines.includes(path)) lines.push(path);
        return { ...f, imagesRaw: lines.join('\n') };
      });
      setModalOnly('success', 'Extra photo saved (appended to “More photos” below).');
    } catch (e) {
      setModalOnly('danger', e.message || 'Upload failed.');
    }
  };

  const handleServerVideoMain = async (file) => {
    if (!file || !uploadServerVideo) return;
    if (!canUploadToServer) {
      setModalOnly('warning', 'Sign in under Admin sign-in to enable server upload.');
      return;
    }
    setModalOnly('info', 'Uploading video…');
    try {
      const path = await uploadServerVideo(file);
      setForm((f) => ({ ...f, video: path }));
      setModalOnly('success', 'Main video saved on server.');
    } catch (e) {
      setModalOnly('danger', e.message || 'Video upload failed.');
    }
  };

  const handleServerVideoAppend = async (file) => {
    if (!file || !uploadServerVideo) return;
    if (!canUploadToServer) {
      setModalOnly('warning', 'Sign in under Admin sign-in to enable server upload.');
      return;
    }
    setModalOnly('info', 'Uploading clip…');
    try {
      const path = await uploadServerVideo(file);
      setForm((f) => {
        const lines = String(f.videosRaw || '')
          .split(/[\r\n,]+/)
          .map((s) => s.trim())
          .filter(Boolean);
        if (!lines.includes(path)) lines.push(path);
        return { ...f, videosRaw: lines.join('\n') };
      });
      setModalOnly('success', 'Extra clip saved on server (appended below).');
    } catch (e) {
      setModalOnly('danger', e.message || 'Video upload failed.');
    }
  };

  const handleUnifiedFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const isVideo =
      (file.type && String(file.type).startsWith('video/')) ||
      /\.(mp4|webm|mov)$/i.test(file.name || '');
    const isImage =
      (file.type && String(file.type).startsWith('image/')) ||
      /\.(jpe?g|png|webp|gif|avif)$/i.test(file.name || '');
    if (mediaKind === 'photo' && isVideo) {
      setModalOnly('warning', 'You picked a video — switch to Video, or choose an image file.');
      return;
    }
    if (mediaKind === 'video' && !isVideo) {
      setModalOnly('warning', 'You picked an image — switch to Photo, or choose a video file (MP4, WebM, MOV).');
      return;
    }
    if (mediaKind === 'photo' && !isImage && !isVideo) {
      setModalOnly('warning', 'Could not detect an image file. Try JPG, PNG, WebP, GIF, or AVIF.');
      return;
    }
    if (mediaKind === 'photo' && mediaSlot === 'main') {
      if (canUploadToServer && uploadServerImage) return handleServerUpload(file);
      return handleEmbed(file);
    }
    if (mediaKind === 'photo' && mediaSlot === 'extra') return handleServerImageAppend(file);
    if (mediaKind === 'video' && mediaSlot === 'main') return handleServerVideoMain(file);
    if (mediaKind === 'video' && mediaSlot === 'extra') return handleServerVideoAppend(file);
  };

  const removeMainPhoto = () => {
    const extras = parseMultiline(form.imagesRaw);
    if (extras.length > 0) {
      setForm((f) => ({ ...f, image: extras[0], imagesRaw: extras.slice(1).join('\n') }));
    } else {
      setForm((f) => ({ ...f, image: '' }));
    }
  };

  const removeExtraPhotoAt = (index) => {
    const extras = parseMultiline(form.imagesRaw);
    if (index < 0 || index >= extras.length) return;
    extras.splice(index, 1);
    setForm((f) => ({ ...f, imagesRaw: extras.join('\n') }));
  };

  const removeMainVideo = () => {
    const extras = parseMultiline(form.videosRaw);
    if (extras.length > 0) {
      setForm((f) => ({ ...f, video: extras[0], videosRaw: extras.slice(1).join('\n') }));
    } else {
      setForm((f) => ({ ...f, video: '' }));
    }
  };

  const removeExtraVideoAt = (index) => {
    const extras = parseMultiline(form.videosRaw);
    if (index < 0 || index >= extras.length) return;
    extras.splice(index, 1);
    setForm((f) => ({ ...f, videosRaw: extras.join('\n') }));
  };

  const addExtraPhotoUrl = () => {
    const u = String(extraPhotoUrlDraft || '').trim();
    if (!u) return;
    setForm((f) => {
      const lines = parseMultiline(f.imagesRaw);
      if (!lines.includes(u)) lines.push(u);
      return { ...f, imagesRaw: lines.join('\n') };
    });
    setExtraPhotoUrlDraft('');
    setModalOnly('success', 'Extra photo path added.');
  };

  const addExtraVideoUrl = () => {
    const u = String(extraVideoUrlDraft || '').trim();
    if (!u) return;
    setForm((f) => {
      const lines = parseMultiline(f.videosRaw);
      if (!lines.includes(u)) lines.push(u);
      return { ...f, videosRaw: lines.join('\n') };
    });
    setExtraVideoUrlDraft('');
    setModalOnly('success', 'Extra video path added.');
  };

  const submit = () => {
    const name = String(form.name || '').trim();
    if (!name) {
      setModalAndCatalogStatus('warning', 'Product name is required.');
      return;
    }
    let sub = String(form.subcategory || '').trim().toLowerCase().replace(/\s+/g, '-');
    sub = sub.replace(/[^a-z0-9_-]/g, '');
    if (!sub) {
      if (subs.length === 0) {
        sub = 'all';
      } else {
        setModalAndCatalogStatus(
          'warning',
          'Pick a subcategory from the list, or choose “Custom…” and type a slug (e.g. kids, bangle).'
        );
        return;
      }
    }
    const mainImg = String(form.image || '').trim();
    const extraImg = parseMultiline(form.imagesRaw);
    const images = [];
    if (mainImg) {
      images.push(mainImg);
      extraImg.forEach((u) => {
        if (u && !images.includes(u)) images.push(u);
      });
    } else {
      extraImg.forEach((u) => {
        if (u && !images.includes(u)) images.push(u);
      });
    }
    const primary = images[0] || '';

    const { videosRaw, imagesRaw, videos: _oldVideos, images: _oldImages, ...rest } = form;
    const videos = parseMultiline(videosRaw);
    const price = parsePriceText(priceDraft);
    const salePrice = computeSalePriceForSave(price, {
      onSale: saleOn,
      salePrice: parsePriceText(salePriceDraft),
      salePercent: salePercentDraft,
    });
    if (saleOn && price > 0 && salePrice === null) {
      setModalAndCatalogStatus(
        'warning',
        'Enter a sale price lower than the regular price, or a discount % between 1 and 99.'
      );
      return;
    }
    onSave({
      ...rest,
      name,
      id: mode === 'add' ? autoProductId : String(form.id || '').trim(),
      type: String(form.type || '').trim(),
      subcategory: sub,
      image: primary,
      images,
      video: String(form.video || '').trim(),
      videos,
      noSound: form.noSound !== false,
      price,
      sale_price: salePrice,
      is_visible: form.is_visible !== false,
      stock_qty: stockUnlimited ? null : Math.max(0, parseInt(stockQtyDraft, 10) || 0),
      char_personalization: Boolean(form.char_personalization),
      char_personalization_letters: String(form.char_personalization_letters || '').trim(),
      gift_tags: normalizeProductGiftTags(form.gift_tags),
      return_allowance: form.return_allowance || 'inherit',
    });
  };

  return (
    <div
      className="modal fade show d-block product-editor-modal-root position-fixed top-0 start-0 w-100 h-100 overflow-auto"
      style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1100 }}
      role="presentation"
    >
      {modalBanner ? (
        <div
          className="position-sticky top-0 start-0 end-0 py-2 px-2 px-sm-3"
          style={{ zIndex: 2 }}
          onClick={(e) => e.stopPropagation()}
          role="alert"
          aria-live="assertive"
        >
          <div className={`alert alert-${modalBanner.variant} py-2 px-3 small mb-0 shadow-sm mx-auto`} style={{ maxWidth: '52rem' }}>
            {modalBanner.text}
          </div>
        </div>
      ) : null}
      <div
        className="modal-dialog modal-lg modal-dialog-scrollable mx-auto my-3"
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-editor-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header">
            <div className="me-auto min-w-0">
              <h5 className="modal-title mb-0" id="product-editor-modal-title">
                {mode === 'add' ? 'Add product' : 'Edit product'}
              </h5>
              <p className="modal-header__category mb-0">
                Category: <strong>{categoryMeta.name}</strong>
              </p>
            </div>
            <button type="button" className="btn-close" aria-label="Close" onClick={requestClose} />
          </div>
          <div className="modal-body">
            <div className="row g-3 product-editor-fields">
              <div className="col-12 col-md-6">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">Regular price (₹)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  className="form-control"
                  placeholder="e.g. 249 or 249.50"
                  value={priceDraft}
                  onChange={(e) => setPriceDraft(normalizePriceTyping(e.target.value))}
                  onBlur={() => {
                    const n = parsePriceText(priceDraft);
                    setPriceDraft(String(parseFloat(n.toFixed(2))));
                  }}
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">
                  Shop group (subcategory) <span className="text-danger">*</span>
                </label>
                <p className="small text-muted mb-1">
                  Groups for <strong>{categoryMeta.name}</strong>
                  {subs.length === 0
                    ? ' — none yet. Add shop groups under Categories, or use Custom slug.'
                    : ` — ${subs.length} group${subs.length === 1 ? '' : 's'}.`}
                </p>
                <select
                  className="form-select"
                  aria-required="true"
                  value={subPick}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '__custom__') {
                      setSubPick('__custom__');
                      setForm((f) => {
                        const cur = String(f.subcategory || '').trim();
                        return { ...f, subcategory: slugInList(subs, cur) ? '' : cur };
                      });
                    } else {
                      setSubPick(v);
                      setForm((f) => ({ ...f, subcategory: v }));
                    }
                  }}
                >
                  {subs.length === 0 ? (
                    <option value="all">All</option>
                  ) : (
                    <option value="">Select…</option>
                  )}
                  {subs.map((s) => (
                    <option key={s.slug} value={s.slug}>
                      {s.name}
                    </option>
                  ))}
                  <option value="__custom__">Custom slug…</option>
                </select>
                {subPick === '__custom__' ? (
                  <input
                    type="text"
                    className="form-control form-control-sm mt-2"
                    placeholder="e.g. mirror, bangle (lowercase slug)"
                    value={form.subcategory}
                    onChange={(e) => setForm((f) => ({ ...f, subcategory: e.target.value }))}
                    onBlur={(e) => {
                      const normalized = String(e.target.value || '')
                        .trim()
                        .toLowerCase()
                        .replace(/\s+/g, '-')
                        .replace(/[^a-z0-9_-]/g, '');
                      setForm((f) => (f.subcategory === normalized ? f : { ...f, subcategory: normalized }));
                    }}
                  />
                ) : null}
              </div>
              {productTypes.length > 0 ? (
                <div className="col-12 col-md-6">
                  <label className="form-label">Product type</label>
                  <select
                    className="form-select"
                    value={String(form.type || '').trim()}
                    onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                  >
                    <option value="">None / inherit category default</option>
                    {productTypes.map((t) => (
                      <option key={t.slug} value={t.slug}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  <p className="small text-muted mb-0 mt-1">
                    Used for return rules when set on the product type in Categories.
                  </p>
                </div>
              ) : null}
              <div className="col-12">
                <label className="form-label" htmlFor="product-gift-tags">
                  Gift finder tags
                </label>
                <input
                  id="product-gift-tags"
                  type="text"
                  className="form-control"
                  placeholder="e.g. evil-eye, kids, premium (comma-separated)"
                  value={Array.isArray(form.gift_tags) ? form.gift_tags.join(', ') : ''}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      gift_tags: normalizeProductGiftTags(e.target.value),
                    }))
                  }
                />
                <p className="small text-muted mb-0 mt-1">
                  Powers gift finder tag matching — separate from subcategory.
                </p>
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">
                  Product ID {mode === 'add' ? <span className="text-muted fw-normal">(auto)</span> : null}
                </label>
                <input
                  type="text"
                  className="form-control font-monospace product-editor-id-input"
                  value={mode === 'add' ? autoProductId || '…' : String(form.id || '')}
                  readOnly
                  aria-readonly="true"
                  tabIndex={-1}
                />
              </div>
              <div className="col-12 product-editor-sale-row">
                <AdminToggle
                  id="product-editor-on-sale"
                  label="On sale"
                  labelClassName="fw-semibold"
                  checked={saleOn}
                  onChange={(checked) => {
                    setSaleOn(checked);
                    if (!checked) {
                      setSalePriceDraft('');
                      setSalePercentDraft('');
                    }
                  }}
                />
                  {saleOn ? (
                    <div className="row g-2 mt-2 product-editor-sale-fields">
                      <div className="col-6 col-md-4">
                        <label className="form-label small mb-1" htmlFor="product-sale-price">
                          Sale price (₹)
                        </label>
                        <input
                          id="product-sale-price"
                          type="text"
                          inputMode="decimal"
                          className="form-control form-control-sm"
                          placeholder="e.g. 299"
                          value={salePriceDraft}
                          onChange={(e) => {
                            setSalePriceDraft(normalizePriceTyping(e.target.value));
                            if (e.target.value.trim()) setSalePercentDraft('');
                          }}
                          onBlur={() => {
                            const n = parsePriceText(salePriceDraft);
                            if (n > 0) setSalePriceDraft(String(parseFloat(n.toFixed(2))));
                          }}
                        />
                      </div>
                      <div className="col-6 col-md-4">
                        <label className="form-label small mb-1" htmlFor="product-sale-pct">
                          Discount %
                        </label>
                        <input
                          id="product-sale-pct"
                          type="text"
                          inputMode="decimal"
                          className="form-control form-control-sm"
                          placeholder="e.g. 15"
                          value={salePercentDraft}
                          onChange={(e) => {
                            setSalePercentDraft(e.target.value.replace(/[^\d.]/g, ''));
                            if (e.target.value.trim()) setSalePriceDraft('');
                          }}
                        />
                      </div>
                  </div>
                ) : null}
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">Stock</label>
                <div className="product-editor-option-box">
                  <AdminToggle
                    id="product-editor-stock-unlimited"
                    label="Unlimited (made to order)"
                    checked={stockUnlimited}
                    onChange={setStockUnlimited}
                  />
                  {!stockUnlimited ? (
                    <input
                      type="number"
                      min={0}
                      max={9999}
                      className="form-control form-control-sm mt-2"
                      value={stockQtyDraft}
                      onChange={(e) => setStockQtyDraft(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      placeholder="Units available"
                    />
                  ) : null}
                </div>
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">Visibility</label>
                <div className="product-editor-option-box product-editor-option-box--switch">
                  <AdminToggle
                    id="product-editor-visible"
                    label="Visible on shop"
                    checked={form.is_visible !== false}
                    onChange={(v) => setForm((f) => ({ ...f, is_visible: v }))}
                  />
                </div>
              </div>
              <div className="col-12">
                <label className="form-label">Letter personalization</label>
                <div className="product-editor-option-box">
                  <AdminToggle
                    id="product-editor-char-personalization"
                    label="Customer picks one letter (A–Z) before add to bag"
                    checked={Boolean(form.char_personalization)}
                    onChange={(v) => setForm((f) => ({ ...f, char_personalization: v }))}
                  />
                  {form.char_personalization ? (
                    <>
                      <label className="form-label small text-muted mt-3 mb-1" htmlFor="product-editor-char-letters">
                        Allowed letters (optional)
                      </label>
                      <input
                        id="product-editor-char-letters"
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Leave empty for A–Z, or e.g. A,B,C,M,R"
                        value={form.char_personalization_letters || ''}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            char_personalization_letters: e.target.value.toUpperCase(),
                          }))
                        }
                      />
                      <p className="small text-muted mb-0 mt-2">
                        Shoppers choose a single letter on the product page. Use a comma-separated list if only some
                        charms are in stock.
                      </p>
                    </>
                  ) : null}
                  <ReturnAllowanceSelect
                    id="product-editor-return-allowance"
                    label="Returns"
                    value={form.return_allowance || 'inherit'}
                    onChange={(v) => setForm((f) => ({ ...f, return_allowance: v }))}
                    hint="Overrides category, shop group, and product type when not set to Inherit."
                    className="mt-3"
                  />
                </div>
              </div>
              <div className="col-12">
                <label className="form-label">Description</label>
                <textarea className="form-control" rows={3} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
              </div>
              <div className="col-12">
                <label className="form-label">Pictures & videos</label>
                <div className="border rounded p-3 mb-3 bg-white">
                  <div className="small fw-semibold text-muted mb-2">Photos</div>
                  <ul className="list-group list-group-flush border rounded small mb-0">
                    {String(form.image || '').trim() ? (
                      <li className="list-group-item d-flex justify-content-between align-items-center gap-2 py-2">
                        <div className="d-flex align-items-center gap-2 min-w-0 flex-grow-1">
                          {editorListThumbSrc(String(form.image || '').trim()) ? (
                            <img
                              src={editorListThumbSrc(String(form.image || '').trim())}
                              alt=""
                              className="rounded border flex-shrink-0 bg-light"
                              width={40}
                              height={40}
                              style={{ objectFit: 'cover' }}
                            />
                          ) : (
                            <span
                              className="flex-shrink-0 rounded bg-light border d-inline-flex align-items-center justify-content-center text-muted"
                              style={{ width: 40, height: 40, fontSize: 10 }}
                            >
                              —
                            </span>
                          )}
                          <span className="text-break">
                            <span className="badge bg-secondary me-1">Cover</span>
                            {String(form.image || '').trim()}
                          </span>
                        </div>
                        <button type="button" className="btn btn-sm btn-outline-danger flex-shrink-0" onClick={removeMainPhoto}>
                          Remove
                        </button>
                      </li>
                    ) : null}
                    {parseMultiline(form.imagesRaw).map((path, i) => (
                      <li key={`extra-img-${i}-${path}`} className="list-group-item d-flex justify-content-between align-items-center gap-2 py-2">
                        <div className="d-flex align-items-center gap-2 min-w-0 flex-grow-1">
                          {editorListThumbSrc(path) ? (
                            <img
                              src={editorListThumbSrc(path)}
                              alt=""
                              className="rounded border flex-shrink-0 bg-light"
                              width={40}
                              height={40}
                              style={{ objectFit: 'cover' }}
                            />
                          ) : (
                            <span
                              className="flex-shrink-0 rounded bg-light border d-inline-flex align-items-center justify-content-center text-muted"
                              style={{ width: 40, height: 40, fontSize: 10 }}
                            >
                              —
                            </span>
                          )}
                          <span className="text-break">
                            <span className="badge bg-light text-dark border me-1">More</span>
                            {path}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger flex-shrink-0"
                          onClick={() => removeExtraPhotoAt(i)}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                    {!String(form.image || '').trim() && parseMultiline(form.imagesRaw).length === 0 ? (
                      <li className="list-group-item text-muted py-3">No photos yet.</li>
                    ) : null}
                  </ul>
                </div>

                <div className="border rounded p-3 mb-3 bg-white">
                  <div className="small fw-semibold text-muted mb-2">Videos</div>
                  <ul className="list-group list-group-flush border rounded small mb-0">
                    {String(form.video || '').trim() ? (
                      <li className="list-group-item d-flex justify-content-between align-items-center gap-2 py-2">
                        <span className="text-break me-2">
                          <span className="badge bg-secondary me-1">Main</span>
                          {String(form.video || '').trim()}
                        </span>
                        <button type="button" className="btn btn-sm btn-outline-danger flex-shrink-0" onClick={removeMainVideo}>
                          Remove
                        </button>
                      </li>
                    ) : null}
                    {parseMultiline(form.videosRaw).map((path, i) => (
                      <li key={`extra-vid-${i}-${path}`} className="list-group-item d-flex justify-content-between align-items-center gap-2 py-2">
                        <span className="text-break me-2">
                          <span className="badge bg-light text-dark border me-1">More</span>
                          {path}
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger flex-shrink-0"
                          onClick={() => removeExtraVideoAt(i)}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                    {!String(form.video || '').trim() && parseMultiline(form.videosRaw).length === 0 ? (
                      <li className="list-group-item text-muted py-3">No videos (optional).</li>
                    ) : null}
                  </ul>
                </div>

                <div className="border rounded p-3 bg-light mb-3 product-editor-upload">
                  <div className="fw-semibold small mb-2">Add a file from your computer</div>
                  <div className="d-flex flex-wrap gap-3 align-items-end product-editor-upload__controls">
                    <div className="product-editor-upload__field">
                      <span className="form-label small text-muted mb-1 d-block">Type</span>
                      <div className="btn-group btn-group-sm" role="group" aria-label="Media type">
                        <button
                          type="button"
                          className={`btn ${mediaKind === 'photo' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setMediaKind('photo')}
                        >
                          Photo
                        </button>
                        <button
                          type="button"
                          className={`btn ${mediaKind === 'video' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setMediaKind('video')}
                        >
                          Video
                        </button>
                      </div>
                    </div>
                    <div className="product-editor-upload__field">
                      <span className="form-label small text-muted mb-1 d-block">Add as</span>
                      <div className="btn-group btn-group-sm" role="group" aria-label="Where to put file">
                        <button
                          type="button"
                          className={`btn ${mediaSlot === 'main' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                          onClick={() => setMediaSlot('main')}
                        >
                          {mediaKind === 'photo' ? 'Cover image' : 'Main clip'}
                        </button>
                        <button
                          type="button"
                          className={`btn ${mediaSlot === 'extra' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                          onClick={() => setMediaSlot('extra')}
                        >
                          {mediaKind === 'photo' ? 'More photos' : 'More clips'}
                        </button>
                      </div>
                    </div>
                    <label
                      className={`btn btn-sm btn-success mb-0 product-editor-upload__pick${fileInputDisabled ? ' disabled' : ''}`}
                      style={fileInputDisabled ? { opacity: 0.65, cursor: 'not-allowed' } : undefined}
                    >
                      Choose file
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif,image/avif,.jpg,.jpeg,.png,.webp,.gif,.avif,video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
                        hidden
                        disabled={fileInputDisabled}
                        onChange={handleUnifiedFile}
                      />
                    </label>
                  </div>
                  {mediaKind === 'video' && !canUploadToServer ? (
                    <p className="small text-warning mb-0">Videos need you signed in to Admin (and PHP running).</p>
                  ) : null}
                  {mediaKind === 'photo' && mediaSlot === 'extra' && !canUploadToServer ? (
                    <p className="small text-warning mb-0">More photos from files need sign-in — or paste a path under “Edit links” below.</p>
                  ) : null}
                  {mediaKind === 'photo' && mediaSlot === 'main' && !canUploadToServer ? (
                    <p className="small text-muted mb-0">Not signed in: cover photo will be stored in the catalog draft only (embed). Sign in to upload to the server.</p>
                  ) : null}
                </div>

                <details className="small border rounded px-3 py-2 mb-3">
                  <summary className="text-muted" style={{ cursor: 'pointer' }}>
                    Edit links (optional)
                  </summary>
                  <div className="mt-3">
                    <label className="form-label small mb-1">Cover photo link or embed</label>
                    <input
                      type="text"
                      className="form-control form-control-sm mb-3"
                      value={form.image}
                      onChange={(e) => setForm((f) => ({ ...f, image: e.target.value }))}
                      placeholder="/folder/photo.avif"
                      autoComplete="off"
                    />
                    <label className="form-label small mb-1">Main video link</label>
                    <input
                      type="text"
                      className="form-control form-control-sm mb-3"
                      value={form.video}
                      onChange={(e) => setForm((f) => ({ ...f, video: e.target.value }))}
                      placeholder="/folder/clip.mp4"
                      autoComplete="off"
                    />
                    <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        style={{ minWidth: '10rem', maxWidth: '24rem' }}
                        placeholder="Paste path for another photo"
                        value={extraPhotoUrlDraft}
                        onChange={(e) => setExtraPhotoUrlDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addExtraPhotoUrl();
                          }
                        }}
                      />
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addExtraPhotoUrl}>
                        Add photo path
                      </button>
                    </div>
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        style={{ minWidth: '10rem', maxWidth: '24rem' }}
                        placeholder="Paste path for another video"
                        value={extraVideoUrlDraft}
                        onChange={(e) => setExtraVideoUrlDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addExtraVideoUrl();
                          }
                        }}
                      />
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addExtraVideoUrl}>
                        Add video path
                      </button>
                    </div>
                  </div>
                </details>
              </div>
              <div className="col-12">
                <AdminToggle
                  id="product-no-sound"
                  className="mb-3"
                  label="Play video muted by default in the shop"
                  checked={form.noSound !== false}
                  onChange={(v) => setForm((f) => ({ ...f, noSound: v }))}
                />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={requestClose}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={submit}>
              {mode === 'add' ? 'Add product' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
